import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { Etiqueta, CanalTemplate, MessageTemplate, TemplateComponent } from '../types';
import { BotAgenteSelector } from './BotAgenteSelector';
import { TooltipHelp } from './TooltipHelp';

interface OfficialApiCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ContactTag {
  id: string;
  nome: string;
}

interface TemplateVariable {
  key: string; // Para POSITIONAL: "1", "2", "3" | Para NAMED: "nome", "produto"
  label: string; // Label amigável para exibir
  value: string; // Valor preenchido pelo usuário
  type: 'header' | 'body' | 'button'; // De qual componente vem
}

export function OfficialApiCampaignModal({ 
  isOpen, 
  onClose, 
  onSuccess 
}: OfficialApiCampaignModalProps) {
  const { theme } = useTheme();
  
  // States
  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    targetEtiquetas: [] as string[],
    headerMediaUrl: '', // URL da mídia para header (imagem, vídeo, documento)
    headerMediaId: '', // ID da mídia selecionada
    headerMediaInputType: 'url' as 'url' | 'upload', // Tipo de input
    headerMediaName: '', // Nome da mídia a ser salva
    randomDelay: 3,
    startImmediately: true,
    scheduledDate: '',
    scheduledTime: '',
  });

  // Vínculo Bot/Agente States
  const [vinculoTipo, setVinculoTipo] = useState<'AGENTE' | 'BOT' | null>(null);
  const [vinculoId, setVinculoId] = useState<string | null>(null);
  const [vinculoName, setVinculoName] = useState<string | null>(null);

  // Filtros de Categoria de Template
  const [templateCategoryFilters, setTemplateCategoryFilters] = useState({
    MARKETING: true,
    UTILITY: true,
    AUTHENTICATION: true
  });

  // Estado para rastrear qual campo de variável está focado
  const [focusedVariableKey, setFocusedVariableKey] = useState<string | null>(null);

  // API Integration States
  const [caixasEntrada, setCaixasEntrada] = useState<CanalTemplate[]>([]);
  const [loadingCaixas, setLoadingCaixas] = useState(false);
  const [selectedCaixa, setSelectedCaixa] = useState<CanalTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<TemplateVariable[]>([]);

  // Existing States
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [selectedEtiquetas, setSelectedEtiquetas] = useState<Etiqueta[]>([]);
  const [loadingEtiquetas, setLoadingEtiquetas] = useState(false);

  // Media Upload States
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [savedMediaList, setSavedMediaList] = useState<any[]>([]);
  const [selectedMediaThumbnail, setSelectedMediaThumbnail] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // Arquivo selecionado mas não enviado ainda
  
  // Delete Media States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<any>(null);
  const [deletingMedia, setDeletingMedia] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (isOpen) {
      loadContactTags();
      loadEtiquetas();
      loadCaixasEntrada();
    }
  }, [isOpen]);

  // Extract variables when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      extractTemplateVariables(selectedTemplate);
    }
  }, [selectedTemplate]);

  // Carregar mídias salvas automaticamente quando template muda
  useEffect(() => {
    if (selectedTemplate) {
      // Verificar se template tem header com mídia
      const headerComponent = selectedTemplate.components.find(c => c.type === 'HEADER');
      const mediaFormat = headerComponent?.format;
      
      if (mediaFormat && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(mediaFormat)) {
        // Mapear formato do template para tipo de mídia no banco
        const mediaTypeFilter = mediaFormat; // IMAGE, VIDEO ou DOCUMENT
        
        // Carregar mídias desse tipo automaticamente
        loadSavedMedia(mediaTypeFilter);
        
        // console.log(`🔄 Template selecionado com header ${mediaFormat}, carregando mídias salvas...`);
      } else {
        // Template não tem mídia no header, limpar lista
        setSavedMediaList([]);
      }
      
      // Limpar seleção de mídia anterior ao trocar de template
      setFormData(prev => ({
        ...prev,
        headerMediaUrl: '',
        headerMediaId: '',
        headerMediaName: ''
      }));
      setSelectedMediaThumbnail(null);
      setSelectedFile(null); // Limpar arquivo selecionado
    }
  }, [selectedTemplate]);

  const loadCaixasEntrada = async () => {
    setLoadingCaixas(true);
    try {
      const tenant = await apiService.getCurrentTenant();
      
      if (!tenant.plataforma || !tenant.idPlataforma) {
        toast.error('Tenant sem plataforma configurada. Configure a plataforma primeiro.');
        return;
      }

      // console.log('📥 Buscando caixas de entrada...', {
      //   plataforma: tenant.plataforma,
      //   accountId: tenant.idPlataforma
      // });

      const caixas = await apiService.getCaixasEntrada(tenant.plataforma, tenant.idPlataforma);
      // Logs sensíveis removidos por segurança (API keys, tokens)
      // console.log('✅ Caixas de entrada carregadas:', caixas?.length, 'caixas encontradas');
      
      setCaixasEntrada(caixas);
      
      // console.log('🔍 Estado atualizado, length:', caixas.length);
      
      if (caixas.length === 0) {
        toast.error('Nenhuma caixa de entrada encontrada para a API Oficial');
      } else {
        toast.success(`${caixas.length} caixa(s) de entrada carregada(s)`);
      }
    } catch (error: any) {
      console.error('❌ Erro ao carregar caixas de entrada:', error);
      toast.error(error.message || 'Erro ao carregar caixas de entrada');
    } finally {
      setLoadingCaixas(false);
    }
  };

  const loadContactTags = async () => {
    try {
      const response = await apiService.getCategories(1, 100);
      setContactTags(response.categories);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadEtiquetas = async () => {
    setLoadingEtiquetas(true);
    try {
      const tenant = await apiService.getCurrentTenant();
      
      if (!tenant.plataforma || !tenant.idPlataforma) {
        return;
      }

      const response = await apiService.getEtiquetas(tenant.plataforma, tenant.idPlataforma);
      setEtiquetas(response.etiquetas);
    } catch (error) {
      console.error('Erro ao carregar etiquetas:', error);
    } finally {
      setLoadingEtiquetas(false);
    }
  };

  const loadSavedMedia = async (mediaType?: string) => {
    try {
      const params = mediaType ? { mediaType } : {};
      const response = await apiService.get('/official-api-media/list', { params });
      setSavedMediaList(response.media || []);
    } catch (error) {
      console.error('Erro ao carregar mídias:', error);
    }
  };

  const extractTemplateVariables = (template: MessageTemplate) => {
    const variables: TemplateVariable[] = [];
    const isPositional = template.parameter_format === 'POSITIONAL';

    template.components.forEach((component: TemplateComponent) => {
      if (!component.text) return;

      // Regex para encontrar variáveis
      // POSITIONAL: {{1}}, {{2}}, {{3}}
      // NAMED: {{nome}}, {{produto}}, {{data}}
      const regex = isPositional ? /\{\{(\d+)\}\}/g : /\{\{(\w+)\}\}/g;
      let match;

      while ((match = regex.exec(component.text)) !== null) {
        const key = match[1];
        const existingVar = variables.find(v => v.key === key);
        
        if (!existingVar) {
          variables.push({
            key: key,
            label: isPositional 
              ? `Variável ${key}` 
              : key.charAt(0).toUpperCase() + key.slice(1),
            value: '',
            type: component.type.toLowerCase() as 'header' | 'body' | 'button'
          });
        }
      }

      // Extrair variáveis de botões com URL dinâmica
      if (component.buttons) {
        component.buttons.forEach((button, idx) => {
          if (button.url && button.url.includes('{{')) {
            const urlRegex = /\{\{(\w+)\}\}/g;
            let urlMatch;
            while ((urlMatch = urlRegex.exec(button.url)) !== null) {
              const key = urlMatch[1];
              if (!variables.find(v => v.key === key)) {
                variables.push({
                  key: key,
                  label: `URL do Botão "${button.text}"`,
                  value: '',
                  type: 'button'
                });
              }
            }
          }
        });
      }
    });

    // Ordenar variáveis posicionais numericamente
    if (isPositional) {
      variables.sort((a, b) => parseInt(a.key) - parseInt(b.key));
    }

    // console.log('📋 Variáveis extraídas:', variables);
    setTemplateVariables(variables);
  };

  const handleCaixaChange = (caixaId: string) => {
    const caixa = caixasEntrada.find(c => c.id.toString() === caixaId);
    setSelectedCaixa(caixa || null);
    setSelectedTemplate(null); // Reset template ao mudar caixa
    setTemplateVariables([]); // Reset variáveis
  };

  const handleTemplateChange = (templateName: string) => {
    const template = selectedCaixa?.message_templates.find(t => t.name === templateName);
    setSelectedTemplate(template || null);
  };

  const handleVariableChange = (key: string, value: string) => {
    setTemplateVariables(prev => 
      prev.map(v => v.key === key ? { ...v, value } : v)
    );
  };

  // Função para inserir variável dinâmica no campo focado
  const insertDynamicVariable = (variable: string) => {
    if (!focusedVariableKey) {
      toast.error('Selecione um campo de variável primeiro');
      return;
    }

    setTemplateVariables(prev => 
      prev.map(v => v.key === focusedVariableKey ? { ...v, value: v.value + variable } : v)
    );
    
    toast.success(`${variable} inserido!`);
  };

  const handleTagToggle = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tagId)
        ? prev.targetTags.filter(id => id !== tagId)
        : [...prev.targetTags, tagId]
    }));
  };

  const handleAddEtiqueta = (etiquetaId: number) => {
    const etiqueta = etiquetas.find(e => e.id === etiquetaId);
    if (etiqueta && !selectedEtiquetas.find(e => e.id === etiquetaId)) {
      const newSelected = [...selectedEtiquetas, etiqueta];
      setSelectedEtiquetas(newSelected);
      setFormData(prev => ({
        ...prev,
        targetEtiquetas: newSelected.map(e => e.title)
      }));
    }
  };

  const handleRemoveEtiqueta = (etiquetaId: number) => {
    const newSelected = selectedEtiquetas.filter(e => e.id !== etiquetaId);
    setSelectedEtiquetas(newSelected);
    setFormData(prev => ({
      ...prev,
      targetEtiquetas: newSelected.map(e => e.title)
    }));
  };

  const handleMediaUpload = async (file: File) => {
    if (!formData.headerMediaName.trim()) {
      toast.error('Digite um nome para a mídia antes de fazer upload');
      return;
    }

    // Validar tipo de arquivo
    const mediaType = selectedTemplate?.components.find(c => c.type === 'HEADER')?.format;
    
    if (mediaType === 'VIDEO') {
      if (file.type !== 'video/mp4') {
        toast.error('❌ Apenas vídeos no formato MP4 são aceitos');
        return;
      }
      if (file.size > 14 * 1024 * 1024) {
        toast.error('❌ Vídeo muito grande. Tamanho máximo: 14 MB');
        return;
      }
    }

    setUploadingMedia(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('name', formData.headerMediaName);

      // Não passar Content-Type manualmente - o navegador define automaticamente com boundary
      const response = await apiService.post('/official-api-media/upload', uploadFormData);

      // console.log('📥 Resposta do upload recebida:', response);
      // console.log('   Media ID:', response.media.id);
      // console.log('   Media Type:', response.media.mediaType);
      // console.log('   FileURL:', response.media.fileUrl);
      // console.log('   ThumbnailURL:', response.media.thumbnailUrl);

      setFormData(prev => ({ 
        ...prev, 
        headerMediaId: response.media.id,
        headerMediaUrl: response.media.fileUrl 
      }));

      // Salvar thumbnail para exibir na pré-visualização
      // console.log('💾 Salvando thumbnail no estado:', response.media.thumbnailUrl);
      setSelectedMediaThumbnail(response.media.thumbnailUrl);

      toast.success('✅ Mídia carregada com sucesso!');
      
      // Recarregar lista de mídias
      const mediaTypeFilter = mediaType === 'IMAGE' ? 'IMAGE' : 
                             mediaType === 'VIDEO' ? 'VIDEO' : 'DOCUMENT';
      await loadSavedMedia(mediaTypeFilter);
      
    } catch (error: any) {
      console.error('Erro no upload:', error);
      toast.error(error.message || 'Erro ao fazer upload');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDeleteMedia = async () => {
    if (!mediaToDelete) return;

    setDeletingMedia(true);

    try {
      await apiService.delete(`/official-api-media/${mediaToDelete.id}`);
      
      toast.success(`✅ Mídia "${mediaToDelete.name}" excluída com sucesso!`);
      
      // Recarregar lista de mídias
      const headerComponent = selectedTemplate?.components.find(c => c.type === 'HEADER');
      const mediaFormat = headerComponent?.format;
      
      if (mediaFormat && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(mediaFormat)) {
        await loadSavedMedia(mediaFormat);
      }
      
      // Se a mídia excluída era a selecionada, limpar seleção
      if (formData.headerMediaId === mediaToDelete.id) {
        setFormData(prev => ({
          ...prev,
          headerMediaUrl: '',
          headerMediaId: '',
          headerMediaName: ''
        }));
        setSelectedMediaThumbnail(null);
      }
      
      setShowDeleteModal(false);
      setMediaToDelete(null);
    } catch (error: any) {
      console.error('Erro ao excluir mídia:', error);
      toast.error(error.message || 'Erro ao excluir mídia');
    } finally {
      setDeletingMedia(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validations
    if (!formData.nome.trim()) {
      toast.error('Nome da campanha é obrigatório');
      return;
    }

    if (!selectedCaixa) {
      toast.error('Selecione uma caixa de entrada');
      return;
    }

    if (!selectedTemplate) {
      toast.error('Selecione um template');
      return;
    }

    if (formData.targetTags.length === 0 && formData.targetEtiquetas.length === 0) {
      toast.error('Selecione pelo menos uma categoria ou etiqueta de contatos');
      return;
    }

    // Validar se todas as variáveis foram preenchidas
    const emptyVariables = templateVariables.filter(v => !v.value.trim());
    if (emptyVariables.length > 0) {
      toast.error(`Preencha todas as variáveis do template: ${emptyVariables.map(v => v.label).join(', ')}`);
      return;
    }

    // Validar mídia do header (se necessário)
    const hasMediaHeader = selectedTemplate.components.some(c => 
      c.type === 'HEADER' && (c.format === 'IMAGE' || c.format === 'VIDEO' || c.format === 'DOCUMENT')
    );
    
    if (hasMediaHeader) {
      if (formData.headerMediaInputType === 'url') {
        // Validar URL manual
        if (!formData.headerMediaUrl.trim()) {
          toast.error('URL da mídia é obrigatória');
          return;
        }
        try {
          new URL(formData.headerMediaUrl);
        } catch {
          toast.error('URL da mídia inválida');
          return;
        }
      } else {
        // Validar upload
        if (!formData.headerMediaId) {
          toast.error('Faça upload da mídia ou selecione uma já existente');
          return;
        }
      }
    }

    if (!formData.startImmediately && (!formData.scheduledDate || !formData.scheduledTime)) {
      toast.error('Defina data e hora para agendamento');
      return;
    }

    try {
      // Preparar variáveis do template
      const templateVars = templateVariables.reduce((acc, v) => {
        acc[v.key] = v.value;
        return acc;
      }, {} as Record<string, string>);

      // Preparar body da requisição
      const requestBody = {
        nome: formData.nome,
        phoneNumberId: selectedCaixa.provider_config.phone_number_id,
        apiKey: selectedCaixa.provider_config.api_key,
        businessAccountId: selectedCaixa.provider_config.business_account_id,
        template: {
          id: selectedTemplate.id,
          name: selectedTemplate.name,
          language: selectedTemplate.language,
          category: selectedTemplate.category,
          components: selectedTemplate.components,
          parameter_format: selectedTemplate.parameter_format
        },
        templateVariables: templateVars,
        headerMediaUrl: hasMediaHeader ? formData.headerMediaUrl : undefined, // URL da mídia do header
        headerMediaId: hasMediaHeader && formData.headerMediaInputType === 'upload' 
          ? formData.headerMediaId 
          : undefined, // ID da mídia salva
        caixaDisparo: selectedCaixa.name,
        numeroCaixaDisparo: selectedCaixa.phone_number,
        targetCategories: formData.targetTags, // IDs de categorias
        targetEtiquetas: formData.targetEtiquetas, // Nomes de etiquetas
        vinculoTipo: vinculoTipo, // ✅ Tipo de vínculo (AGENTE ou BOT)
        vinculoId: vinculoId, // ✅ ID do agente ou bot
        vinculoName: vinculoName, // ✅ Nome do agente ou título do bot
        randomDelay: formData.randomDelay,
        startImmediately: formData.startImmediately,
        scheduledFor: formData.startImmediately 
          ? null 
          : (() => {
              // ✅ Criar data no timezone local do navegador
              const localDate = new Date(`${formData.scheduledDate}T${formData.scheduledTime}:00`);
              // Retornar ISO string (já no timezone correto)
              return localDate.toISOString();
            })()
      };

      // Log de criação (dados sensíveis omitidos)
      // console.log('📤 Enviando campanha da API Oficial:', {
      //   nome: requestBody.nome,
      //   totalContacts: requestBody.targetCategories?.length || 0,
      //   template: requestBody.template?.name
      // });

      const response = await apiService.post('/campaigns/official-api', requestBody);

      // console.log('✅ Campanha criada com sucesso!');

      if (response.success) {
        toast.success(`Campanha criada! ${response.totalContacts} contato(s) na fila`);
        onSuccess();
        resetForm();
        onClose();
      } else {
        throw new Error(response.error || 'Erro ao criar campanha');
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar campanha:', error);
      toast.error(error.message || error.error || 'Erro ao criar campanha da API Oficial');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      targetTags: [],
      targetEtiquetas: [],
      headerMediaUrl: '',
      headerMediaId: '',
      headerMediaInputType: 'url',
      headerMediaName: '',
      randomDelay: 3,
      startImmediately: true,
      scheduledDate: '',
      scheduledTime: '',
    });
    setSelectedEtiquetas([]);
    setSelectedCaixa(null);
    setSelectedTemplate(null);
    setTemplateVariables([]);
    setSavedMediaList([]);
    setSelectedMediaThumbnail(null);
    setSelectedFile(null);
    setVinculoTipo(null);
    setVinculoId(null);
    setVinculoName(null);
  };

  // Handler para seleção de Bot/Agente
  const handleVinculoSelect = (tipo: 'AGENTE' | 'BOT' | null, id: string | null, name: string | null) => {
    setVinculoTipo(tipo);
    setVinculoId(id);
    setVinculoName(name);
  };

  const renderTemplatePreview = () => {
    if (!selectedTemplate) return null;

    // console.log('🖼️ Renderizando pré-visualização');
    // console.log('   selectedMediaThumbnail:', selectedMediaThumbnail);

    return (
      <div className={`mt-4 p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h5 className={`text-sm font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          📱 Pré-visualização do Template
        </h5>
        
        <div className={`space-y-2 p-3 rounded ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
          {selectedTemplate.components.map((component, idx) => {
            if (component.type === 'HEADER') {
              if (component.text) {
                return (
                  <div key={idx} className="font-bold text-sm">
                    {component.text}
                  </div>
                );
              } else if (component.format === 'IMAGE') {
                return (
                  <div key={idx} className={`rounded text-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    {selectedMediaThumbnail ? (
                      <div className="relative">
                        <img 
                          src={selectedMediaThumbnail} 
                          alt="Preview da imagem" 
                          className="w-full h-auto max-h-60 object-contain rounded"
                        />
                        <div className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs ${theme === 'dark' ? 'bg-black/70 text-white' : 'bg-white/90 text-gray-800'}`}>
                          🖼️ Imagem do Header
                        </div>
                      </div>
                    ) : (
                      <div className={`p-4 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        🖼️ Imagem do Header
                      </div>
                    )}
                  </div>
                );
              } else if (component.format === 'VIDEO') {
                return (
                  <div key={idx} className={`rounded text-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    {selectedMediaThumbnail ? (
                      <div className="relative">
                        <img 
                          src={selectedMediaThumbnail} 
                          alt="Preview do vídeo" 
                          className="w-full h-auto max-h-60 object-contain rounded"
                        />
                        <div className={`absolute inset-0 flex items-center justify-center`}>
                          <div className="w-16 h-16 bg-black/70 rounded-full flex items-center justify-center">
                            <span className="text-white text-3xl">▶</span>
                          </div>
                        </div>
                        <div className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs ${theme === 'dark' ? 'bg-black/70 text-white' : 'bg-white/90 text-gray-800'}`}>
                          🎥 Vídeo do Header
                        </div>
                      </div>
                    ) : (
                      <div className={`p-4 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        🎥 Vídeo do Header
                      </div>
                    )}
                  </div>
                );
              } else if (component.format === 'DOCUMENT') {
                return (
                  <div key={idx} className={`p-4 rounded text-center text-xs ${theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                    📄 Documento do Header
                    {formData.headerMediaUrl && (
                      <div className="mt-2 text-xs break-all">
                        <a href={formData.headerMediaUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                          Ver URL
                        </a>
                      </div>
                    )}
                  </div>
                );
              }
            }
            if (component.type === 'BODY' && component.text) {
              return (
                <div key={idx} className="text-sm whitespace-pre-wrap">
                  {component.text}
                </div>
              );
            }
            if (component.type === 'FOOTER' && component.text) {
              return (
                <div key={idx} className="text-xs text-gray-500 italic">
                  {component.text}
                </div>
              );
            }
            if (component.type === 'BUTTONS' && component.buttons) {
              return (
                <div key={idx} className="mt-2 space-y-1">
                  {component.buttons.map((button, btnIdx) => (
                    <div 
                      key={btnIdx}
                      className={`text-center py-1 px-2 rounded text-xs font-medium ${
                        theme === 'dark' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}
                    >
                      {button.text}
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          })}
        </div>

        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
            {selectedTemplate.status}
          </span>
          <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            {selectedTemplate.category}
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            {selectedTemplate.language}
          </span>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto transition-colors bg-theme-card">
        {/* Header */}
        <div className={`flex justify-between items-center p-6 border-b transition-colors ${theme === 'dark' ? '' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`} style={{
          borderColor: theme === 'dark' ? '#717070' : 'var(--border-color)',
          background: theme === 'dark' ? '#404044' : undefined
        }}>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-button)' }}>
                <span className="text-2xl">✅</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-theme-text flex items-center gap-2">
                  <span>Nova Campanha - API Oficial WhatsApp</span>
                  <TooltipHelp id="campaigns_official_create" />
                </h3>
                <p className="text-sm mt-1 text-theme-text-secondary">
                  Configure sua campanha usando templates aprovados da API Oficial
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="hover:bg-opacity-80 p-2 rounded-full transition-colors"
            style={{ color: theme === 'dark' ? '#ffffff' : 'var(--text-muted)' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            
            {/* Nome da Campanha */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                Nome da Campanha *
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                style={{
                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                  borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                  color: theme === 'dark' ? 'white' : '#1f2937'
                }}
                placeholder="Ex: Promoção Black Friday 2024"
                required
              />
            </div>

            {/* Selecionar Caixa de Entrada */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                Selecionar caixa de entrada *
              </label>
              <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                Escolha qual número da API Oficial será usado para enviar
              </p>
              
              {loadingCaixas ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Carregando caixas de entrada...</span>
                </div>
              ) : caixasEntrada.length > 0 ? (
                <>
                  <select
                    value={selectedCaixa?.id.toString() || ''}
                    onChange={(e) => handleCaixaChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    style={{
                      backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                      borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                      color: theme === 'dark' ? 'white' : '#1f2937'
                    }}
                    required
                  >
                    <option value="">Selecionar caixa de entrada...</option>
                    {caixasEntrada.map((caixa) => (
                      <option key={caixa.id} value={caixa.id}>
                        {caixa.name} ({caixa.phone_number})
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  {/* MODIFICADO: Alerta sem caixas com tema */}
                  {/* ANTES: bg-red-50, border-red-300, text-red-* fixos */}
                  <div className="text-center py-8 rounded-md" style={{ backgroundColor: theme === 'dark' ? 'rgba(239,68,68,0.08)' : '#fef2f2', border: `1px solid ${theme === 'dark' ? 'rgba(239,68,68,0.25)' : '#fecaca'}` }}>
                    <p className="font-medium" style={{ color: theme === 'dark' ? '#fecaca' : '#b91c1c' }}>⚠️ Nenhuma caixa de entrada encontrada</p>
                    <p className="text-sm mt-1" style={{ color: theme === 'dark' ? '#fecaca' : '#ef4444' }}>Configure a plataforma no cadastro do tenant</p>
                  </div>
                </>
              )}
            </div>

            {/* Selecionar Template */}
            {selectedCaixa && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                  Modelo do WhatsApp *
                </label>
                <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                  Selecione um modelo para usar nesta campanha
                </p>

                {/* Filtros de Categoria */}
                <div className="mb-4">
                  <p className={`text-xs font-medium mb-2 ${theme === 'light' ? 'text-gray-600' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                    Filtrar por categoria:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {/* Filtro MARKETING */}
                    <button
                      type="button"
                      onClick={() => setTemplateCategoryFilters(prev => ({ ...prev, MARKETING: !prev.MARKETING }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        templateCategoryFilters.MARKETING
                          ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                          : theme === 'dark' 
                            ? 'bg-gray-700 text-gray-400 border-2 border-gray-600' 
                            : 'bg-gray-200 text-gray-500 border-2 border-gray-300'
                      }`}
                    >
                      {templateCategoryFilters.MARKETING ? '✓' : '○'}
                      <span>Marketing</span>
                    </button>

                    {/* Filtro UTILITY */}
                    <button
                      type="button"
                      onClick={() => setTemplateCategoryFilters(prev => ({ ...prev, UTILITY: !prev.UTILITY }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        templateCategoryFilters.UTILITY
                          ? 'bg-green-100 text-green-700 border-2 border-green-500'
                          : theme === 'dark' 
                            ? 'bg-gray-700 text-gray-400 border-2 border-gray-600' 
                            : 'bg-gray-200 text-gray-500 border-2 border-gray-300'
                      }`}
                    >
                      {templateCategoryFilters.UTILITY ? '✓' : '○'}
                      <span>Utilidade</span>
                    </button>

                    {/* Filtro AUTHENTICATION */}
                    <button
                      type="button"
                      onClick={() => setTemplateCategoryFilters(prev => ({ ...prev, AUTHENTICATION: !prev.AUTHENTICATION }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        templateCategoryFilters.AUTHENTICATION
                          ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                          : theme === 'dark' 
                            ? 'bg-gray-700 text-gray-400 border-2 border-gray-600' 
                            : 'bg-gray-200 text-gray-500 border-2 border-gray-300'
                      }`}
                    >
                      {templateCategoryFilters.AUTHENTICATION ? '✓' : '○'}
                      <span>Autenticação</span>
                    </button>
                  </div>
                </div>
                
                {selectedCaixa.message_templates.length > 0 ? (
                  (() => {
                    // Filtrar templates por categoria e status
                    const filteredTemplates = selectedCaixa.message_templates
                      .filter(t => t.status === 'APPROVED')
                      .filter(t => {
                        // Se nenhum filtro estiver ativo, não mostra nada
                        const hasAnyFilterActive = templateCategoryFilters.MARKETING || 
                                                  templateCategoryFilters.UTILITY || 
                                                  templateCategoryFilters.AUTHENTICATION;
                        
                        if (!hasAnyFilterActive) {
                          return false;
                        }

                        // Retorna true se o template pertence a uma categoria ativa
                        return templateCategoryFilters[t.category as keyof typeof templateCategoryFilters];
                      });

                    return filteredTemplates.length > 0 ? (
                      <select
                        value={selectedTemplate?.name || ''}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        size={Math.min(filteredTemplates.length + 1, 10)}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors overflow-y-auto"
                        style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                          borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                          color: theme === 'dark' ? 'white' : '#1f2937',
                          maxHeight: '250px'
                        }}
                        required
                      >
                        <option value="">Selecione um modelo...</option>
                        {filteredTemplates.map((template) => (
                          <option key={template.id} value={template.name}>
                            {template.name} ({template.language})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-center py-4 rounded-md" style={{ backgroundColor: theme === 'dark' ? 'rgba(250,204,21,0.08)' : '#fffbeb', border: `1px solid ${theme === 'dark' ? 'rgba(250,204,21,0.25)' : '#fde68a'}` }}>
                        <p style={{ color: theme === 'dark' ? '#fde68a' : '#a16207' }}>
                          {selectedCaixa.message_templates.filter(t => t.status === 'APPROVED').length === 0
                            ? '⚠️ Nenhum template aprovado disponível para esta caixa'
                            : '⚠️ Nenhum template encontrado com as categorias selecionadas'}
                        </p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-4 rounded-md" style={{ backgroundColor: theme === 'dark' ? 'rgba(250,204,21,0.08)' : '#fffbeb', border: `1px solid ${theme === 'dark' ? 'rgba(250,204,21,0.25)' : '#fde68a'}` }}>
                    <p style={{ color: theme === 'dark' ? '#fde68a' : '#a16207' }}>⚠️ Nenhum template aprovado disponível para esta caixa</p>
                  </div>
                )}

                {renderTemplatePreview()}
              </div>
            )}

            {/* URL da Mídia do Header (se houver) */}
            {selectedTemplate && selectedTemplate.components.some(c => 
              c.type === 'HEADER' && (c.format === 'IMAGE' || c.format === 'VIDEO' || c.format === 'DOCUMENT')
            ) && (
              <div>
                {(() => {
                  const headerComponent = selectedTemplate.components.find(c => c.type === 'HEADER');
                  const mediaType = headerComponent?.format;
                  const mediaTypeLabel = mediaType === 'IMAGE' ? 'Imagem' : 
                                        mediaType === 'VIDEO' ? 'Vídeo' : 
                                        'Documento';
                  const mediaTypeIcon = mediaType === 'IMAGE' ? '🖼️' : 
                                       mediaType === 'VIDEO' ? '🎥' : 
                                       '📄';
                  
                  return (
                    <>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        {mediaTypeIcon} {mediaTypeLabel} (Header) *
                      </label>

                      {/* Toggle URL / Upload */}
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, headerMediaInputType: 'url' }))}
                          className={`px-4 py-2 rounded ${formData.headerMediaInputType === 'url' 
                            ? 'bg-blue-500 text-white' 
                            : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                        >
                          🔗 URL Manual
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, headerMediaInputType: 'upload' }))}
                          className={`px-4 py-2 rounded ${formData.headerMediaInputType === 'upload' 
                            ? 'bg-blue-500 text-white' 
                            : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                        >
                          📤 Fazer Upload
                        </button>
                      </div>

                      {formData.headerMediaInputType === 'url' ? (
                        // Campo URL Manual
                        <>
                          <p className={`text-xs mb-2 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                            Insira a URL pública do {mediaTypeLabel.toLowerCase()}
                          </p>
                          <input
                            type="url"
                            value={formData.headerMediaUrl}
                            onChange={(e) => setFormData(prev => ({ ...prev, headerMediaUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                            placeholder={`https://exemplo.com/${mediaTypeLabel.toLowerCase()}.${mediaType === 'IMAGE' ? 'jpg' : 'mp4'}`}
                          />
                          <p className={`text-xs mt-2 ${theme === 'light' ? 'text-yellow-600' : 'text-yellow-400'}`}>
                            ⚠️ A URL deve ser pública e acessível pela API do WhatsApp
                          </p>
                        </>
                      ) : (
                        <>
                          {/* MODIFICADO: Seção de Upload */}
                          {/* ANTES: bg-white e textos fixos */}
                          <p className={`text-xs mb-2 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                            Faça upload ou selecione uma mídia já salva
                          </p>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm">Nome da Mídia *</label>
                              <input
                                type="text"
                                value={formData.headerMediaName}
                                onChange={(e) => setFormData(prev => ({ ...prev, headerMediaName: e.target.value }))}
                                placeholder="Ex: Promoção Natal 2024"
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                style={{
                                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                  borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                  color: theme === 'dark' ? 'white' : '#1f2937'
                                }}
                              />
                            </div>

                            <div>
                              <label className="text-sm">Arquivo *</label>
                              <input
                                type="file"
                                accept={mediaType === 'IMAGE' ? 'image/jpeg,image/jpg,image/png' : 
                                        mediaType === 'VIDEO' ? 'video/mp4' : 
                                        'application/pdf'}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    console.log('📂 Arquivo selecionado:', file.name);
                                    setSelectedFile(file);
                                  }
                                }}
                                disabled={uploadingMedia || !formData.headerMediaName.trim()}
                                className="w-full px-3 py-2 border rounded-md text-sm transition-colors"
                                style={{
                                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                  borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                  color: theme === 'dark' ? 'white' : '#1f2937'
                                }}
                              />
                              
                              {mediaType === 'VIDEO' && (
                                <p className="text-xs text-orange-500 mt-2">
                                  ⚠️ Apenas vídeos MP4 com até 14 MB
                                </p>
                              )}
                              
                              {/* Botão de Upload */}
                              {selectedFile && !formData.headerMediaId && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    console.log('🚀 Botão de upload clicado!');
                                    handleMediaUpload(selectedFile);
                                  }}
                                  disabled={uploadingMedia}
                                  className={`mt-3 px-4 py-2 rounded transition-all ${
                                    uploadingMedia
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                                  }`}
                                >
                                  {uploadingMedia ? (
                                    <span className="flex items-center gap-2">
                                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Enviando... Aguarde
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-2">
                                      📤 {mediaType === 'IMAGE' ? 'Salvar imagem' : mediaType === 'VIDEO' ? 'Salvar vídeo' : 'Salvar documento'}
                                    </span>
                                  )}
                                </button>
                              )}
                              
                              {formData.headerMediaId && formData.headerMediaUrl && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                                  <p className="text-sm text-green-700 font-semibold flex items-center gap-2">
                                    <span className="text-xl">✅</span>
                                    {mediaType === 'IMAGE' ? 'Imagem salva com sucesso!' : mediaType === 'VIDEO' ? 'Vídeo salvo com sucesso!' : 'Documento salvo com sucesso!'}
                                  </p>
                                  <p className="text-xs text-green-600 mt-1">
                                    {formData.headerMediaName}
                                  </p>
                                </div>
                              )}
                            </div>

                          </div>
                        </>
                      )}
                    </>
                  );
                })()}

                {/* Mídias Salvas - SEMPRE VISÍVEL quando houver mídias */}
                {savedMediaList.length > 0 && (
                  <div className="mt-4">
                    <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                      📂 Mídias Salvas ({savedMediaList.length})
                    </label>
                    <p className={`text-xs mb-2 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                      Clique em uma mídia para reutilizá-la nesta campanha
                    </p>
                    
                    <div className={`max-h-60 overflow-y-auto border rounded p-2 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}>
                      {savedMediaList.map(media => {
                        console.log('📋 Renderizando mídia na lista:', media.name, 'ID:', media.id);
                        return (
                        <div 
                          key={media.id}
                          className={`flex items-center gap-3 p-2 rounded mb-2 relative ${
                            formData.headerMediaId === media.id 
                              ? theme === 'dark' ? 'bg-blue-900/30 border-2 border-blue-500' : 'bg-blue-50 border-2 border-blue-500'
                              : theme === 'dark' ? 'hover:bg-gray-700 border-2 border-transparent' : 'hover:bg-gray-100 border-2 border-transparent'
                          }`}
                        >
                          <div 
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                            style={{ pointerEvents: 'auto' }}
                            onClick={(e) => {
                              try {
                                console.log('🎯 CLICK DETECTADO na mídia!');
                                console.log('   Nome:', media?.name || 'UNDEFINED');
                                console.log('   Objeto media completo:', media);
                                
                                if (!media) {
                                  console.error('❌ Objeto media é undefined/null');
                                  toast.error('Erro: mídia não encontrada');
                                  return;
                                }
                                
                                if (!media.id || !media.fileUrl) {
                                  console.error('❌ Media faltando campos obrigatórios:', {
                                    id: media.id,
                                    fileUrl: media.fileUrl,
                                    thumbnailUrl: media.thumbnailUrl
                                  });
                                  toast.error('Erro: dados da mídia incompletos');
                                  return;
                                }
                                
                                console.log('✅ Validação OK, atualizando estado...');
                                
                                setFormData(prev => {
                                  console.log('   Estado anterior headerMediaId:', prev.headerMediaId);
                                  const newState = {
                                    ...prev,
                                    headerMediaId: media.id,
                                    headerMediaUrl: media.fileUrl,
                                    headerMediaName: media.name,
                                    headerMediaInputType: 'upload' as const
                                  };
                                  console.log('   Novo estado headerMediaId:', newState.headerMediaId);
                                  return newState;
                                });
                                
                                console.log('💾 Salvando thumbnail no estado:', media.thumbnailUrl);
                                setSelectedMediaThumbnail(media.thumbnailUrl);
                                
                                console.log('✅ Estado atualizado com sucesso!');
                                toast.success(`✅ Mídia "${media.name}" selecionada`);
                              } catch (error: any) {
                                console.error('❌ ERRO CRÍTICO ao selecionar mídia:', error);
                                console.error('   Mensagem:', error?.message);
                                console.error('   Stack:', error?.stack);
                                toast.error('Erro ao selecionar mídia: ' + (error?.message || 'Desconhecido'));
                              }
                            }}
                          >
                            {media.thumbnailUrl && (
                              <img src={media.thumbnailUrl} alt="" className="w-12 h-12 object-cover rounded border" />
                            )}
                            {!media.thumbnailUrl && media.mediaType === 'VIDEO' && (
                              <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                <span className="text-2xl">🎥</span>
                              </div>
                            )}
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                                {media.name}
                              </p>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {(media.size / 1024 / 1024).toFixed(2)} MB • {new Date(media.createdAt).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                            {formData.headerMediaId === media.id && (
                              <div className="flex items-center gap-1">
                                <span className="text-green-500 text-xl">✓</span>
                                <span className={`text-xs font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                  Selecionada
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Botão de Excluir */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMediaToDelete(media);
                              setShowDeleteModal(true);
                            }}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              theme === 'dark' 
                                ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
                                : 'bg-red-50 text-red-600 hover:bg-red-100'
                            }`}
                            title="Excluir mídia"
                          >
                            🗑️ Excluir
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Variáveis do Template */}
            {templateVariables.length > 0 && (
              <div>
                <label className={`block text-sm font-medium mb-3 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                  Variáveis *
                </label>
                <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                  Preencha os valores para cada variável do template. Você pode usar as variáveis dinâmicas abaixo:
                </p>
                
                {/* Variáveis Dinâmicas Disponíveis - CLICÁVEIS */}
                <div className={`mb-4 p-3 rounded-lg border ${theme === 'dark' ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'}`}>
                  <div className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-900'}`}>
                    💡 Variáveis Dinâmicas Disponíveis (clique para inserir)
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[
                      { var: '{{nome}}', label: 'Nome completo' },
                      { var: '{{primeiroNome}}', label: 'Primeiro nome' },
                      { var: '{{telefone}}', label: 'Telefone' },
                      { var: '{{email}}', label: 'E-mail' },
                      { var: '{{categoria}}', label: 'Categoria' },
                      { var: '{{observacoes}}', label: 'Observações' }
                    ].map(({ var: variable, label }) => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => insertDynamicVariable(variable)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          theme === 'dark' 
                            ? 'bg-blue-800/50 text-blue-200 hover:bg-blue-700/60 hover:scale-105' 
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 hover:scale-105'
                        } cursor-pointer shadow-sm hover:shadow-md`}
                        title={`Clique para inserir ${variable}`}
                      >
                        <span className="font-mono">{variable}</span>
                        <span className={`ml-1.5 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}>
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                    <strong>💡 Como usar:</strong> Clique em um campo de variável abaixo, depois clique na variável desejada para inseri-la.
                  </div>
                  <div className={`mt-1 text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    <strong>Exemplo:</strong> "Olá {'{'}{'{'} primeiroNome {'}'}{'}'}, seu telefone é {'{'}{'{'} telefone {'}'}{'}'}."
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templateVariables.map((variable) => (
                    <div key={variable.key}>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        {variable.label} * {focusedVariableKey === variable.key && <span className="text-green-500 text-xs">✓ Campo ativo</span>}
                      </label>
                      <input
                        type="text"
                        value={variable.value}
                        onChange={(e) => handleVariableChange(variable.key, e.target.value)}
                        onFocus={() => setFocusedVariableKey(variable.key)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${
                          focusedVariableKey === variable.key 
                            ? 'border-green-500 focus:ring-green-500 ring-2 ring-green-200' 
                            : 'focus:ring-blue-500'
                        }`}
                        style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                          borderColor: focusedVariableKey === variable.key 
                            ? '#10b981' 
                            : theme === 'dark' ? '#3F3F46' : '#d1d5db',
                          color: theme === 'dark' ? 'white' : '#1f2937'
                        }}
                        placeholder={`Insira o valor ou clique em uma variável dinâmica acima`}
                        required
                      />
                      <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Componente: {variable.type.toUpperCase()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bot e Agente de IA — DESATIVADO TEMPORARIAMENTE (descomentar quando necessário)
            <div>
              <BotAgenteSelector
                selectedVinculoTipo={vinculoTipo}
                selectedVinculoId={vinculoId}
                selectedVinculoName={vinculoName}
                onSelect={handleVinculoSelect}
              />
            </div>
            */}

            {/* Categorias de Contatos */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                Categorias de Contatos
              </label>
              <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                Selecione quais categorias de contatos receberão a campanha
              </p>
              <div className="max-h-40 overflow-y-auto border rounded-md p-3 transition-colors" style={{
                backgroundColor: theme === 'dark' ? '#2A2A2A' : '#f9fafb',
                borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db'
              }}>
                {contactTags.map((tag) => (
                  <label key={tag.id} className="flex items-center space-x-2 mb-2 p-2 rounded cursor-pointer transition-colors" style={{
                    '--hover-bg': theme === 'dark' ? '#3F3F46' : 'white'
                  } as React.CSSProperties}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3F3F46' : 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.targetTags.includes(tag.id)}
                      onChange={() => handleTagToggle(tag.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium" style={{ color: theme === 'dark' ? 'var(--text-primary)' : '#374151' }}>{tag.nome}</span>
                  </label>
                ))}
                {contactTags.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria disponível</p>
                )}
              </div>
            </div>

            {/* Etiquetas da Plataforma */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                Etiquetas da Plataforma
              </label>
              <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                Selecione etiquetas da sua plataforma para filtrar contatos
              </p>
              
              {selectedEtiquetas.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3 p-3 rounded-lg border border-gray-300 bg-gray-50">
                  {selectedEtiquetas.map((etiqueta) => (
                    <div
                      key={etiqueta.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
                      style={{
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        border: '1px solid #7dd3fc'
                      }}
                    >
                      <span>{etiqueta.title}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEtiqueta(etiqueta.id)}
                        className="hover:bg-red-100 hover:text-red-600 rounded-full p-0.5 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {loadingEtiquetas ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : etiquetas.length > 0 ? (
                <select
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value) {
                      handleAddEtiqueta(value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  style={{
                    backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                    borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                    color: theme === 'dark' ? 'white' : '#1f2937'
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Selecione uma etiqueta...</option>
                  {etiquetas
                    .filter(etiq => !selectedEtiquetas.find(s => s.id === etiq.id))
                    .map((etiq) => (
                      <option key={etiq.id} value={etiq.id}>
                        {etiq.title}
                      </option>
                    ))}
                </select>
              ) : null}
            </div>

            {/* Configuração de Envio */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                Tempo de Randomização (segundos)
              </label>
              <input
                type="number"
                min="0"
                max="300"
                value={formData.randomDelay}
                onChange={(e) => setFormData(prev => ({ ...prev, randomDelay: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                style={{
                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                  borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                  color: theme === 'dark' ? 'white' : '#1f2937'
                }}
              />
              <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                ⏱️ Intervalo entre envios: {formData.randomDelay > 0 ? `${formData.randomDelay}s` : '1s (mínimo)'} • Recomendado: 3-5s
              </p>
            </div>

            {/* Quando Enviar */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                Quando Enviar
              </label>
              <div className="space-y-3">
                {/* MODIFICADO: Opções de envio com tema */}
                {/* ANTES: bg-white, text-gray-* */}
                <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer" style={{ backgroundColor: 'var(--bg-card)', borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)' }}>
                  <input
                    type="radio"
                    checked={formData.startImmediately}
                    onChange={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>Enviar Imediatamente</div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>A campanha começará assim que for criada</div>
                  </div>
                </label>
                <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer" style={{ backgroundColor: 'var(--bg-card)', borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)' }}>
                  <input
                    type="radio"
                    checked={!formData.startImmediately}
                    onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Agendar para depois</div>
                    {!formData.startImmediately && (
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={formData.scheduledDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                          className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white', borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)', color: theme === 'dark' ? 'white' : '#1f2937' }}
                          min={new Date().toISOString().split('T')[0]}
                          required={!formData.startImmediately}
                        />
                        <input
                          type="time"
                          value={formData.scheduledTime}
                          onChange={(e) => setFormData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                          className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white', borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)', color: theme === 'dark' ? 'white' : '#1f2937' }}
                          required={!formData.startImmediately}
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t" style={{ borderColor: theme === 'dark' ? '#717070' : 'var(--border-color)' }}>
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 rounded-lg font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--primary-button)' }}
            >
              Criar Campanha API Oficial
            </button>
          </div>
        </form>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && mediaToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Thumbnail da Mídia */}
            <div className="flex justify-center mb-4">
              {mediaToDelete.thumbnailUrl ? (
                <img 
                  src={mediaToDelete.thumbnailUrl} 
                  alt={mediaToDelete.name}
                  className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300"
                />
              ) : (
                <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                  <span className="text-5xl">
                    {mediaToDelete.mediaType === 'VIDEO' ? '🎥' : 
                     mediaToDelete.mediaType === 'IMAGE' ? '🖼️' : '📄'}
                  </span>
                </div>
              )}
            </div>

            {/* Título e Mensagem */}
            <h3 className="text-xl font-semibold mb-3 text-center text-n-slate-12">
              Confirmar Exclusão
            </h3>
            
            <p className={`text-center mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Tem certeza que deseja excluir {mediaToDelete.mediaType === 'IMAGE' ? 'a imagem' : mediaToDelete.mediaType === 'VIDEO' ? 'o vídeo' : 'o documento'}{' '}
              <strong className="font-bold">{mediaToDelete.name}</strong>?
            </p>

            {/* Botões */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setMediaToDelete(null);
                }}
                disabled={deletingMedia}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteMedia}
                disabled={deletingMedia}
                className="flex-1 px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingMedia ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
